import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const DEFAULT_TITLE = 'SeaSalt Creative by Princy Patel | Photography & Videographer Website Design Studio';
const DEFAULT_DESCRIPTION = 'SeaSalt Creative by Princy Patel crafts elevated, intentional, and responsive websites for photographers, videographers, creative studios, and wellness brands.';
const DEFAULT_KEYWORDS = 'princy patel, princy, web designer princy patel, princy seasalt creative, sea, salt, creative, seasalt creative, seasalt, seasalt creative studio, seasalt creative web design, photography website design, photographer websites, videographer web design, creative studio website, portfolio design for photographers, videography portfolio';
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://seasaltcreative.com';

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  image,
  type = 'website',
  schemaData,
}) => {
  const { pathname } = useLocation();
  const fullTitle = title ? `${title} | SeaSalt Creative` : DEFAULT_TITLE;
  const canonicalUrl = `${SITE_URL}${pathname}`;

  useEffect(() => {
    // 1. Update Document Title
    document.title = fullTitle;

    // Helper: Meta tag updater
    const updateMeta = (selector, nameType, nameVal, contentVal) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(nameType, nameVal);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', contentVal);
    };

    // Helper: Link tag updater
    const updateLink = (relVal, hrefVal) => {
      let tag = document.querySelector(`link[rel="${relVal}"]`);
      if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', relVal);
        document.head.appendChild(tag);
      }
      tag.setAttribute('href', hrefVal);
    };

    // 2. Standard Meta Tags
    updateMeta('meta[name="author"]', 'name', 'author', 'Princy Patel - SeaSalt Creative');
    updateMeta('meta[name="description"]', 'name', 'description', description);
    updateMeta('meta[name="keywords"]', 'name', 'keywords', keywords || DEFAULT_KEYWORDS);

    // 3. Open Graph Tags
    updateMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle);
    updateMeta('meta[property="og:description"]', 'property', 'og:description', description);
    updateMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    updateMeta('meta[property="og:type"]', 'property', 'og:type', type);
    updateMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'SeaSalt Creative');

    if (image) {
      const fullImg = image.startsWith('http') ? image : `${SITE_URL}${image}`;
      updateMeta('meta[property="og:image"]', 'property', 'og:image', fullImg);
      updateMeta('meta[name="twitter:image"]', 'name', 'twitter:image', fullImg);
    }

    // 4. Twitter Card Tags
    updateMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    updateMeta('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle);
    updateMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);

    // 5. Canonical Link
    updateLink('canonical', canonicalUrl);

    // 6. JSON-LD Structured Data Schema
    let scriptTag = document.querySelector('#json-ld-schema');
    if (schemaData) {
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('id', 'json-ld-schema');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(schemaData);
    } else if (scriptTag) {
      scriptTag.remove();
    }
  }, [fullTitle, description, keywords, image, type, canonicalUrl, schemaData]);

  return null;
};

export default SEO;
